import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  ArrowLeft, Upload, Trash2, Activity, MapPin, User, Gauge, Loader2,
  Trophy, BarChart3, AlertCircle, Video,
} from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  ReferenceLine, ReferenceDot,
} from 'recharts';
import { API } from '../config/api';
import SatelliteTiles from './SatelliteTiles';
import { ANNOTATION_ICONS, iconFor } from './AnnotationsPanel';

// Race-Studio-style lap colours. Magenta for the reference lap, white for
// the comparison lap. Used across charts AND the circuit map so the same
// lap is always the same colour no matter which channel you're looking at.
const LAP_COLOR_A = '#ec4899';   // magenta / pink (lap A)
const LAP_COLOR_B = '#ffffff';   // white         (lap B)

const CATEGORY_LABELS = {
  turismo_pista: 'Turismo Pista',
  carshow_santafesino: 'CarShow Santafesino',
  turismo_nacional: 'Turismo Nacional',
  simracing: 'Simracing',
};

const fmtLap = (sec) => {
  if (!sec || sec <= 0) return '—';
  const m = Math.floor(sec / 60);
  const s = sec - m * 60;
  return m > 0 ? `${m}:${s.toFixed(3).padStart(6, '0')}` : `${s.toFixed(3)}`;
};

// Tints a temperature value when it crosses warn/danger thresholds.
const tempTint = (v, warn, danger) => {
  if (v == null) return 'text-white';
  if (v >= danger) return 'text-red-400';
  if (v >= warn) return 'text-yellow-400';
  return 'text-white';
};

// Compact metric box used across the telemetry detail view.
function StatBox({ label, value, unit, tint }) {
  const display = value == null || value === '' ? '—' : value;
  return (
    <div className="bg-neutral-950 rounded-md px-2 py-1.5">
      <div className="text-[10px] text-gray-500 uppercase truncate">{label}</div>
      <div className={`font-mono text-base ${tint || 'text-white'}`}>
        {display} {unit && <span className="text-[10px] text-gray-500">{unit}</span>}
      </div>
    </div>
  );
}

// G-value → orange/red heat scale for the circuit map
const gToColor = (g) => {
  const abs = Math.min(Math.abs(g) / 2.0, 1); // 2g = max
  // Low = blue, high = red
  if (abs < 0.5) {
    const t = abs / 0.5;
    const r = Math.round(60 + t * (255 - 60));
    return `rgb(${r}, ${Math.round(180 - t * 80)}, ${Math.round(250 - t * 180)})`;
  }
  const t = (abs - 0.5) / 0.5;
  return `rgb(255, ${Math.round(100 - t * 100)}, ${Math.round(70 - t * 70)})`;
};

export default function TelemetryPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const linkEventId = searchParams.get('event');
  const linkTandaKey = searchParams.get('tanda');
  const isLinkMode = !!(linkEventId && linkTandaKey);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);   // full session with laps[].samples
  const [lapA, setLapA] = useState(null);           // selected reference lap number
  const [lapB, setLapB] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [linking, setLinking] = useState(false);
  // Overlay channel for the speed-trace chart (DEPRECATED — kept for compat).
  const [overlayChannel, setOverlayChannel] = useState('');
  // Multi-channel comparison plots (Race-Studio style stack).
  const ALL_CHANNELS = [
    { key: 'speed_kmh',       label: 'Velocidad',  unit: 'km/h', color: '#60a5fa' },
    { key: 'rpm',             label: 'RPM',        unit: 'rpm',  color: '#a78bfa' },
    { key: 'lat_g',           label: 'G lateral',  unit: 'g',    color: '#fbbf24' },
    { key: 'long_g',          label: 'G long.',    unit: 'g',    color: '#4ade80' },
    { key: 'yaw_rate_dps',    label: 'Yaw Rate',   unit: 'deg/s', color: '#fb7185' },
    { key: 'water_temp_c',    label: 'Agua',       unit: '°C',   color: '#22d3ee' },
    { key: 'exhaust_temp_c',  label: 'Escape',     unit: '°C',   color: '#f87171' },
    { key: 'height_m',        label: 'Altitud',    unit: 'm',    color: '#94a3b8' },
  ];
  // Human-readable reason why a channel may not be present in a given file.
  // Shown as a tooltip when the user hovers a disabled toggle so the absence
  // is *explained*, not just hidden.
  const CHANNEL_UNAVAILABLE_HINT = {
    speed_kmh:      'No detectado en este archivo (deberías tenerlo siempre — revisá el formato).',
    rpm:            'Requiere ECU/tacómetro conectado al data logger. Los VBO Racelogic GPS-only no lo traen.',
    lat_g:          'Requiere IMU/acelerómetro en el logger.',
    long_g:         'Requiere IMU/acelerómetro en el logger.',
    yaw_rate_dps:   'Requiere giróscopo en el logger (VBOX 3i/AIM con IMU). Si tu sesión es vieja, re-subí el archivo: el parser anterior no lo guardaba.',
    water_temp_c:   'Requiere sensor de temperatura de agua conectado al logger.',
    exhaust_temp_c: 'Requiere sonda EGT/termocupla conectada al logger.',
    height_m:       'Requiere fix GPS válido durante la sesión.',
  };
  const [visibleChannels, setVisibleChannels] = useState(['speed_kmh']);
  // Channel used to colour the circuit map heatmap.
  const [mapChannel, setMapChannel] = useState('lat_g');
  const [meta, setMeta] = useState({ pilot: '', category: 'turismo_pista', name: '' });
  const [pickingStart, setPickingStart] = useState(false);
  const [pendingStart, setPendingStart] = useState(null); // {lat, lon, x, y}
  const [recutting, setRecutting] = useState(false);
  const [recutRadius, setRecutRadius] = useState(15);     // m, configurable
  const [showSatellite, setShowSatellite] = useState(false);
  // Synchronised cursor (Race-Studio style): when the user hovers over any
  // chart, this distance value is shared with every other chart and with
  // the circuit map, drawing a vertical line + a track dot at the same point.
  const [hoverDistance, setHoverDistance] = useState(null);
  // Pinned cursor (the explicit selection that drives annotations + range
  // tools). Set via left-click on any chart. Survives mouse-leave; cleared
  // with ESC or by clicking on a different chart spot. Carries the
  // selected distance AND the resolved time_s + lap so a saved annotation
  // can be navigated from Replay Studio without any backfill.
  const [pinnedSelection, setPinnedSelection] = useState(null);  // {distance, time_s, lap_number}
  // Shared distance-axis zoom range. `null` means "fit to data". When set
  // to [min, max] all channel charts + the delta chart constrain their X
  // domain to that window so the user can focus on a sector / corner.
  const [zoomRange, setZoomRange] = useState(null);
  // Track-map zoom level (1 = fit-to-data). Applied via SVG viewBox scaling
  // around the cursor (or the centre when no cursor).
  const [mapZoom, setMapZoom] = useState(1);
  // Coaching annotations attached to the current session (from server). The
  // user can add/delete them from the plots or the track map.
  const [annotations, setAnnotations] = useState([]);
  const [annText, setAnnText] = useState('');
  const [showAnnInput, setShowAnnInput] = useState(false);
  // Distance snapshotted when the user clicked "+ Anotación". We can't keep
  // relying on `hoverDistance` because the mouse leaves the chart as soon as
  // it moves to the button / input, which clears the hover state.
  const [annDistance, setAnnDistance] = useState(null);
  // Colour the user picks for the annotation. Defaults to green; the picker
  // shows a small palette of high-contrast colours so the ingeniero can
  // tag what KIND of fix each note is about (red = brake, blue = line,
  // yellow = throttle, etc).
  const [annColor, setAnnColor] = useState('#22c55e');
  // BUG-6 fix: the DAQ creator now also exposes the shared icon catalogue
  // (same `ANNOTATION_ICONS` as Replay Studio) so a single note carries
  // both a colour AND a semantic tag. The colour is auto-synced to the
  // icon's default when the icon changes, but the user can override it.
  const [annIcon, setAnnIcon] = useState('note');
  // BUG-5 fix: visibility filter — "all" or a specific lap number.
  // Persisted only for the current session.
  const [annFilterLap, setAnnFilterLap] = useState('current'); // 'all' | 'current'
  // BUG-4 fix: track which annotation is being edited inline, and the
  // working draft for that row. Only one row is edited at a time.
  const [editingAnnId, setEditingAnnId] = useState(null);
  const [editingAnnText, setEditingAnnText] = useState('');
  const fileInputRef = useRef(null);

  const applyDaqToTanda = async () => {
    if (!selected || !lapA || !isLinkMode) return;
    setLinking(true);
    try {
      const res = await axios.post(
        `${API}/telemetry/sessions/${selected.session_id}/link-tanda`,
        {
          event_id: linkEventId,
          tanda_key: linkTandaKey,
          lap_number: lapA,
        },
        { withCredentials: true },
      );
      const a = res.data.applied || {};
      toast.success(
        `Tanda actualizada — best ${a.best_time}, ${a.fast_laps} rápidas / ${a.slow_laps} lentas, max ${a.max_speed_kmh} km/h`,
      );
      // Bring the user back to the Pit Engineer event so they can verify the
      // changes. We use window.close() if this is a popup tab, else navigate.
      setTimeout(() => {
        if (window.opener) window.close();
        else navigate('/pit-engineer');
      }, 1200);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo aplicar a la tanda');
    } finally {
      setLinking(false);
    }
  };

  useEffect(() => {
    loadSessions();
  }, []);

  const loadSessions = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/telemetry/sessions`, { withCredentials: true });
      setSessions(res.data.sessions || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al cargar sesiones de telemetría');
    } finally {
      setLoading(false);
    }
  };

  const openSession = async (sid) => {
    try {
      const res = await axios.get(
        `${API}/telemetry/sessions/${sid}?include_samples=true`,
        { withCredentials: true },
      );
      setSelected(res.data);
      const fastest = res.data.fastest_lap_number || res.data.laps[0]?.lap_number;
      setLapA(fastest);
      setLapB(null);
      setComparison(null);
      setPickingStart(false);
      setPendingStart(null);
      setZoomRange(null);
      setMapZoom(1);
      setAnnotations(Array.isArray(res.data.annotations) ? res.data.annotations : []);
      setShowAnnInput(false);
      setAnnText('');
    } catch (err) {
      toast.error('Error al cargar la sesión');
    }
  };

  const saveAnnotation = async () => {
    if (!selected || annDistance == null || !annText.trim()) return;
    try {
      const res = await axios.post(
        `${API}/telemetry/sessions/${selected.session_id}/annotations`,
        {
          distance: annDistance,
          // Pull the resolved time + lap from the pinned selection so the
          // saved annotation already carries everything Replay Studio
          // needs to seek the video — no back-fill required.
          time_s: pinnedSelection?.time_s ?? null,
          text: annText.trim(),
          lap_number: pinnedSelection?.lap_number ?? lapA,
          color: annColor,
          // BUG-6 + future classification model: send the icon key so the
          // same note shows the same emoji + label in Replay Studio.
          icon: annIcon,
        },
        { withCredentials: true },
      );
      setAnnotations((prev) => [...prev, res.data.annotation]);
      setAnnText('');
      setShowAnnInput(false);
      setAnnDistance(null);
      // Keep the pin until the user moves on — supports rapid create of
      // several notes at the same corner without re-clicking the chart.
      toast.success('Anotación guardada', { duration: 1200 });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo guardar la anotación');
    }
  };

  // BUG-4 fix: PATCH an existing annotation in place. Called from the
  // inline edit affordance on each row. Updates both server and local state.
  const editAnnotation = async (annId, patch) => {
    if (!selected) return;
    try {
      const res = await axios.patch(
        `${API}/telemetry/sessions/${selected.session_id}/annotations/${annId}`,
        patch,
        { withCredentials: true },
      );
      setAnnotations((prev) => prev.map((a) => (a.id === annId ? res.data.annotation : a)));
      toast.success('Anotación actualizada', { duration: 900 });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo actualizar');
    }
  };

  const deleteAnnotation = async (annId) => {
    if (!selected) return;
    // BUG-7 fix: confirm before deleting — matches the Replay Studio UX
    // so the engineer doesn't accidentally lose a coaching note.
    if (!window.confirm('¿Borrar esta anotación?')) return;
    try {
      await axios.delete(
        `${API}/telemetry/sessions/${selected.session_id}/annotations/${annId}`,
        { withCredentials: true },
      );
      setAnnotations((prev) => prev.filter((a) => a.id !== annId));
    } catch (err) {
      toast.error('No se pudo borrar la anotación');
    }
  };

  // Recut the laps using the manually picked start point. Sends the raw
  // lat/lon (arc-minutes for VBO) to the backend; on success reloads the
  // session so the new laps appear in the lap-A/B selectors.
  const recutWithStart = async () => {
    if (!selected || !pendingStart) return;
    setRecutting(true);
    try {
      const res = await axios.post(
        `${API}/telemetry/sessions/${selected.session_id}/recut-laps`,
        { start_lat: pendingStart.lat, start_lon: pendingStart.lon, radius_m: recutRadius },
        { withCredentials: true },
      );
      const bestSec = res.data.fastest_lap_s;
      const bestStr = bestSec != null
        ? `${Math.floor(bestSec / 60)}:${(bestSec % 60).toFixed(3).padStart(6, '0')}`
        : '—';
      toast.success(`Re-cortado: ${res.data.lap_count} vueltas — mejor ${bestStr}`);
      setPickingStart(false);
      setPendingStart(null);
      await openSession(selected.session_id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo re-cortar la sesión');
    } finally {
      setRecutting(false);
    }
  };

  const onFilePicked = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    if (meta.name) fd.append('name', meta.name);
    if (meta.pilot) fd.append('pilot', meta.pilot);
    if (meta.category) fd.append('category', meta.category);
    try {
      const res = await axios.post(`${API}/telemetry/upload`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`Telemetría cargada: ${res.data.session.lap_count} vueltas detectadas`);
      setMeta({ pilot: '', category: 'turismo_pista', name: '' });
      await loadSessions();
      // Auto-open the new session
      await openSession(res.data.session.session_id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al procesar el archivo');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const deleteSession = async (sid, e) => {
    e?.stopPropagation();
    if (!window.confirm('¿Eliminar esta sesión de telemetría?')) return;
    try {
      await axios.delete(`${API}/telemetry/sessions/${sid}`, { withCredentials: true });
      toast.success('Eliminada');
      if (selected?.session_id === sid) setSelected(null);
      loadSessions();
    } catch (err) {
      toast.error('No se pudo eliminar');
    }
  };

  // Recompute comparison whenever both laps are picked
  useEffect(() => {
    if (!selected || !lapA || !lapB || lapA === lapB) {
      setComparison(null);
      return;
    }
    (async () => {
      try {
        const res = await axios.get(
          `${API}/telemetry/sessions/${selected.session_id}/compare?lap_a=${lapA}&lap_b=${lapB}`,
          { withCredentials: true },
        );
        setComparison(res.data.comparison);
      } catch (err) {
        toast.error('Error al comparar las vueltas');
        setComparison(null);
      }
    })();
  }, [selected, lapA, lapB]);

  // Build chart data for speed trace
  const lapAData = selected?.laps?.find((l) => l.lap_number === lapA);
  const lapBData = selected?.laps?.find((l) => l.lap_number === lapB);

  // Pin a distance on the current lap. Resolves the matching `time_s` via
  // binary-search on `lapAData.samples.distance_m`, so a saved annotation
  // already carries the value the Replay Studio needs to seek the video
  // (no back-fill required).
  //
  // The dependency on `lapAData` means re-running this lookup is cheap;
  // the closure captures the lap selected at the time of click.
  const pinDistance = (distance) => {
    if (!Number.isFinite(distance)) return;
    let time_s = null;
    const samples = lapAData?.samples;
    if (samples?.distance_m && samples?.time_s
        && samples.distance_m.length === samples.time_s.length
        && samples.distance_m.length > 1) {
      const ds = samples.distance_m;
      let lo = 0, hi = ds.length - 1;
      while (lo < hi - 1) {
        const m = (lo + hi) >> 1;
        if (ds[m] <= distance) lo = m; else hi = m;
      }
      time_s = samples.time_s[lo];
    }
    setPinnedSelection({ distance, time_s, lap_number: lapA });
  };

  // Clear pinned selection with the ESC key. Bound on `selected` so we
  // don't pollute global listeners when the page is showing the list view.
  useEffect(() => {
    if (!selected) return;
    const onKey = (e) => {
      if (e.key === 'Escape' && pinnedSelection) {
        setPinnedSelection(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selected, pinnedSelection]);

  // For each visible channel, build a [{distance, valA, valB}] array indexed
  // by lap A distance. Lap B is resampled onto A by nearest-distance match.
  const channelCharts = useMemo(() => {
    if (!lapAData) return [];
    const a = lapAData.samples;
    const b = lapBData?.samples;
    const aDist = a.distance_m || [];
    return visibleChannels
      .map((key) => {
        const meta = ALL_CHANNELS.find((c) => c.key === key);
        if (!meta || !Array.isArray(a[key])) return null;
        const rows = [];
        for (let i = 0; i < aDist.length; i++) {
          const row = { distance: aDist[i], valA: a[key][i] };
          if (b && Array.isArray(b[key]) && Array.isArray(b.distance_m)) {
            // Resample B onto A's distance basis (nearest neighbour).
            let bestJ = 0, bestD = Infinity;
            for (let j = 0; j < b.distance_m.length; j++) {
              const dd = Math.abs(b.distance_m[j] - aDist[i]);
              if (dd < bestD) { bestD = dd; bestJ = j; }
              else if (b.distance_m[j] > aDist[i] + 100) break;
            }
            row.valB = b[key][bestJ];
          }
          rows.push(row);
        }
        return { ...meta, rows };
      })
      .filter(Boolean);
  }, [lapAData, lapBData, visibleChannels]);

  // Helper for the synchronised cursor: given a target distance and a row
  // array [{distance, valA, valB}], return the row whose distance is closest.
  const valueAt = (rows, dist) => {
    if (dist == null || !rows || rows.length === 0) return null;
    let bestI = 0, bestD = Infinity;
    for (let i = 0; i < rows.length; i++) {
      const d = Math.abs(rows[i].distance - dist);
      if (d < bestD) { bestD = d; bestI = i; }
      else if (rows[i].distance > dist + 200) break;
    }
    return rows[bestI];
  };

  // Position on the circuit map for the synchronised cursor.
  const cursorOnMap = useMemo(() => {
    if (hoverDistance == null || !lapAData?.samples?.distance_m) return null;
    const arr = lapAData.samples.distance_m;
    let bestI = 0, bestD = Infinity;
    for (let i = 0; i < arr.length; i++) {
      const d = Math.abs(arr[i] - hoverDistance);
      if (d < bestD) { bestD = d; bestI = i; }
      else if (arr[i] > hoverDistance + 200) break;
    }
    return { i: bestI, lat: lapAData.samples.lat?.[bestI], lon: lapAData.samples.lon?.[bestI] };
  }, [hoverDistance, lapAData]);

  const speedTraceData = useMemo(() => {
    if (!lapAData) return [];
    const a = lapAData.samples;
    const b = lapBData?.samples;
    const result = [];
    const aDist = a.distance_m || [];
    const aOverlay = (overlayChannel && a[overlayChannel]) || null;
    // For simplicity, overlay laps by distance — when the user picks a second lap
    // we also pre-resample its speed onto the first lap's distance basis.
    for (let i = 0; i < aDist.length; i++) {
      const point = {
        distance: aDist[i],
        speedA: a.speed_kmh[i],
      };
      if (aOverlay) point.overlay = aOverlay[i];
      if (b) {
        // Find the b sample whose distance is closest to aDist[i].
        // O(n*m) but n,m ≤ ~1500 — acceptable for offline charts.
        let bestJ = 0;
        let bestD = Infinity;
        for (let j = 0; j < b.distance_m.length; j++) {
          const dd = Math.abs(b.distance_m[j] - aDist[i]);
          if (dd < bestD) { bestD = dd; bestJ = j; }
          else if (b.distance_m[j] > aDist[i] + 100) break;
        }
        point.speedB = b.speed_kmh[bestJ];
      }
      result.push(point);
    }
    return result;
  }, [lapAData, lapBData, overlayChannel]);

  // (Deprecated) overlay sources for the legacy speed chart. Kept harmless;
  // the new multi-channel stack supersedes this.
  const availableOverlays = [];
  const overlayInfo = null;

  const deltaChartData = useMemo(() => {
    if (!comparison) return [];
    return comparison.distance_m.map((d, i) => ({
      distance: d,
      delta: comparison.delta_s[i],
    }));
  }, [comparison]);

  // ============== CIRCUIT MAP (SVG, colored by selected channel) ===========
  const circuitSvg = useMemo(() => {
    if (!lapAData?.samples?.lat || !lapAData?.samples?.lon) return null;
    const lats = lapAData.samples.lat;
    const lons = lapAData.samples.lon;
    if (lats.length < 5) return null;
    const minLat = Math.min(...lats), maxLat = Math.max(...lats);
    const minLon = Math.min(...lons), maxLon = Math.max(...lons);
    const dLat = maxLat - minLat || 1e-9;
    const dLon = maxLon - minLon || 1e-9;
    // Aspect-preserved viewbox. VBO files store lat/lon in arc-minutes, but
    // the longitudinal scale needs to be corrected by cos(latitude) so the
    // drawing is geometrically accurate.
    // Sign convention: Racelogic VBO stores longitude as POSITIVE-WEST
    // (opposite of the modern +East convention). For southern-hemisphere
    // Argentina the lat values come in negative arc-minutes (-2040 for
    // Buenos Aires) and lon as POSITIVE (+3480 for 58°W). When that's the
    // case we mirror the X-axis so the rendered map matches a standard
    // north-up / east-right view.
    const meanLatDeg = ((minLat + maxLat) / 2) / 60.0;
    const lonScale = Math.cos(meanLatDeg * Math.PI / 180);
    const W = 800;
    const aspect = dLat / (dLon * lonScale);
    const H = Math.max(250, Math.min(550, W * aspect));
    const pad = 20;
    const isVbo = selected?.format === 'vbo';
    const flipX = isVbo && (minLon + maxLon) / 2 > 0;
    const project = (lat, lon) => [
      flipX
        ? pad + ((maxLon - lon) / dLon) * (W - 2 * pad)
        : pad + ((lon - minLon) / dLon) * (W - 2 * pad),
      // SVG y is inverted (north = top)
      H - pad - ((lat - minLat) / dLat) * (H - 2 * pad),
    ];
    // Convert the file's native lat/lon units into decimal degrees so we can
    // align Web-Mercator satellite tiles to the trajectory.
    //  - VBO: arc-minutes; longitude convention is positive-west → negate when
    //    flipX is active (Argentina / Western hemisphere).
    //  - CSV (AIM, ETC): already in decimal degrees.
    const toGeoLat = isVbo ? ((l) => l / 60) : ((l) => l);
    const toGeoLon = isVbo
      ? ((l) => (flipX ? -l / 60 : l / 60))
      : ((l) => l);
    const geoMinLat = Math.min(toGeoLat(minLat), toGeoLat(maxLat));
    const geoMaxLat = Math.max(toGeoLat(minLat), toGeoLat(maxLat));
    const geoMinLon = Math.min(toGeoLon(minLon), toGeoLon(maxLon));
    const geoMaxLon = Math.max(toGeoLon(minLon), toGeoLon(maxLon));
    // Project from decimal degrees → svg (used by the satellite layer).
    const projectGeo = (lat, lon) => {
      if (isVbo) {
        const nativeLat = lat * 60;
        const nativeLon = flipX ? -lon * 60 : lon * 60;
        return project(nativeLat, nativeLon);
      }
      return project(lat, lon);
    };
    // Pixels per metre (used for the recut radius circle).
    const latToM = 111320; // metres per degree of latitude
    const dyPerMeter = Math.abs(
      project(toGeoLat ? 0 : 0, 0)[1] - project(0, 0)[1] // ignored — fallback below
    );
    // Simpler: compute by projecting two close lat/lon points 10 m apart.
    const c0 = project((minLat + maxLat) / 2, (minLon + maxLon) / 2);
    const dLatNative = isVbo ? (10 / latToM) * 60 : (10 / latToM);
    const c1 = project((minLat + maxLat) / 2 + dLatNative, (minLon + maxLon) / 2);
    const pxPerMeter = Math.abs(c1[1] - c0[1]) / 10 || 0.05;
    return {
      W, H, project, lats, lons,
      geoBBox: { minLat: geoMinLat, maxLat: geoMaxLat, minLon: geoMinLon, maxLon: geoMaxLon },
      projectGeo,
      pxPerMeter,
      _dyPerMeter: dyPerMeter, // kept to avoid unused-var warnings
    };
  }, [lapAData, selected]);

  // ============== UI =====================================================
  if (selected) {
    return (
      <div className="min-h-screen bg-black text-white">
        <header className="border-b border-neutral-800 bg-neutral-900/60 sticky top-0 z-10 backdrop-blur">
          <div className="container mx-auto px-3 md:px-6 py-3 flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelected(null)}
              data-testid="telemetry-back-btn"
              className="text-gray-400"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Sesiones
            </Button>
            <div className="flex-1 min-w-0">
              <h1 className="text-lg font-bold text-white truncate flex items-center gap-2">
                <Activity className="w-4 h-4 text-blue-500 flex-shrink-0" />
                {selected.name}
              </h1>
              <div className="text-xs text-gray-500 flex items-center gap-3">
                {selected.circuit && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{selected.circuit}</span>}
                {selected.pilot && <span className="flex items-center gap-1"><User className="w-3 h-3" />{selected.pilot}</span>}
                <span>· {selected.format?.toUpperCase()} @ {selected.rate_hz} Hz</span>
                <span>· {selected.lap_count} vueltas</span>
              </div>
            </div>
          </div>
        </header>

        <main className="container mx-auto px-3 md:px-6 py-4 space-y-4">
          {isLinkMode && (
            <Card className="bg-green-950/30 border-green-700/50">
              <CardContent className="p-3 flex items-center gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-green-300 font-semibold">
                    Modo enlace — Pit Engineer
                  </div>
                  <div className="text-xs text-green-400/80">
                    Esta sesión se va a aplicar a la tanda <span className="font-mono text-white">{linkTandaKey}</span> del evento <span className="font-mono text-white">{linkEventId}</span>.
                    Elegí abajo la vuelta que querés usar como "mejor tiempo" y presioná aplicar.
                  </div>
                </div>
                <Button
                  onClick={applyDaqToTanda}
                  disabled={linking || !lapA}
                  data-testid="apply-daq-to-tanda"
                  className="bg-green-600 hover:bg-green-700"
                >
                  {linking ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Activity className="w-4 h-4 mr-1.5" />}
                  Aplicar a la tanda
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Lap selector */}
          <Card className="bg-neutral-900 border-neutral-800">
            <CardContent className="p-3 flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <span
                  className="inline-block w-3 h-3 rounded-sm"
                  style={{ backgroundColor: LAP_COLOR_A }}
                  data-testid="lap-a-color"
                />
                <div className="text-xs text-gray-400">Vuelta A (referencia):</div>
              </div>
              <select
                value={lapA || ''}
                onChange={(e) => setLapA(Number(e.target.value))}
                data-testid="lap-a-select"
                className="bg-neutral-800 border border-neutral-700 text-white text-sm rounded px-2 py-1.5"
                style={{ borderLeft: `3px solid ${LAP_COLOR_A}` }}
              >
                {selected.laps.map((l) => (
                  <option key={l.lap_number} value={l.lap_number}>
                    Lap {l.lap_number} — {fmtLap(l.lap_time_s)}
                    {l.lap_number === selected.fastest_lap_number ? ' 🏆' : ''}
                  </option>
                ))}
              </select>
              <div className="flex items-center gap-2">
                <span
                  className="inline-block w-3 h-3 rounded-sm border border-neutral-600"
                  style={{ backgroundColor: lapB ? LAP_COLOR_B : 'transparent' }}
                  data-testid="lap-b-color"
                />
                <div className="text-xs text-gray-400">Vuelta B (comparación):</div>
              </div>
              <select
                value={lapB || ''}
                onChange={(e) => setLapB(e.target.value === '' ? null : Number(e.target.value))}
                data-testid="lap-b-select"
                className="bg-neutral-800 border border-neutral-700 text-white text-sm rounded px-2 py-1.5"
                style={lapB ? { borderLeft: `3px solid ${LAP_COLOR_B}` } : {}}
              >
                <option value="">— Sin comparar —</option>
                {selected.laps.map((l) => (
                  <option key={l.lap_number} value={l.lap_number}>
                    Lap {l.lap_number} — {fmtLap(l.lap_time_s)}
                  </option>
                ))}
              </select>
              {hoverDistance != null && (
                <div className="ml-auto text-[11px] text-gray-400 font-mono" data-testid="hover-distance-label">
                  {hoverDistance.toFixed(0)} m
                </div>
              )}
            </CardContent>
          </Card>

          {/* Lap stats card — per-lap engine + dynamics metrics */}
          {lapAData && (
            <Card className="bg-neutral-900 border-neutral-800">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-blue-400" />
                  Vuelta {lapAData.lap_number} — métricas
                  <span className="text-xs text-gray-500 ml-2 font-normal">
                    tiempo: <span className="font-mono text-white">{fmtLap(lapAData.lap_time_s)}</span>
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2 text-xs">
                  <StatBox label="Vel. máx" value={lapAData.max_speed_kmh} unit="km/h" />
                  <StatBox label="Vel. media" value={lapAData.avg_speed_kmh} unit="km/h" />
                  <StatBox label="RPM máx" value={lapAData.max_rpm} unit="rpm" />
                  <StatBox label="Agua máx" value={lapAData.max_water_temp_c} unit="°C" tint={tempTint(lapAData.max_water_temp_c, 90, 110)} />
                  <StatBox label="Escape máx" value={lapAData.max_exhaust_temp_c} unit="°C" tint={tempTint(lapAData.max_exhaust_temp_c, 750, 900)} />
                  <StatBox label="G lat. acum." value={lapAData.lat_g_accumulated} unit="g·s" />
                  <StatBox label="Distancia" value={lapAData.distance_m ? (lapAData.distance_m / 1000).toFixed(2) : null} unit="km" />
                </div>
              </CardContent>
            </Card>
          )}

          {/* Session summary — aggregates across all laps */}
          {selected.laps && selected.laps.length > 1 && (
            <Card className="bg-neutral-900 border-neutral-800">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Trophy className="w-4 h-4 text-yellow-400" />
                  Resumen de la sesión completa ({selected.laps.length} vueltas)
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2 text-xs">
                  <StatBox
                    label="Mejor vuelta"
                    value={fmtLap(Math.min(...selected.laps.map((l) => l.lap_time_s)))}
                    unit=""
                  />
                  <StatBox
                    label="Vel. máx sesión"
                    value={Math.max(...selected.laps.map((l) => l.max_speed_kmh || 0))}
                    unit="km/h"
                  />
                  <StatBox
                    label="RPM máx sesión"
                    value={
                      selected.laps.some((l) => l.max_rpm)
                        ? Math.max(...selected.laps.map((l) => l.max_rpm || 0))
                        : null
                    }
                    unit="rpm"
                  />
                  <StatBox
                    label="Agua máx sesión"
                    value={
                      selected.laps.some((l) => l.max_water_temp_c)
                        ? Math.max(...selected.laps.map((l) => l.max_water_temp_c || 0))
                        : null
                    }
                    unit="°C"
                  />
                  <StatBox
                    label="Escape máx sesión"
                    value={
                      selected.laps.some((l) => l.max_exhaust_temp_c)
                        ? Math.max(...selected.laps.map((l) => l.max_exhaust_temp_c || 0))
                        : null
                    }
                    unit="°C"
                  />
                  <StatBox
                    label="G lat. acum. total"
                    value={
                      selected.laps.reduce((a, l) => a + (l.lat_g_accumulated || 0), 0).toFixed(1)
                    }
                    unit="g·s"
                  />
                  <StatBox
                    label="Distancia total"
                    value={(selected.laps.reduce((a, l) => a + (l.distance_m || 0), 0) / 1000).toFixed(2)}
                    unit="km"
                  />
                </div>
                {/* Per-lap table */}
                <div className="mt-3 overflow-x-auto">
                  <table className="w-full text-xs font-mono">
                    <thead>
                      <tr className="text-[10px] uppercase text-gray-500 border-b border-neutral-800">
                        <th className="text-left py-1.5 px-2">Lap</th>
                        <th className="text-right py-1.5 px-2">Tiempo</th>
                        <th className="text-right py-1.5 px-2">Vmax</th>
                        <th className="text-right py-1.5 px-2">RPM</th>
                        <th className="text-right py-1.5 px-2">Agua</th>
                        <th className="text-right py-1.5 px-2">Escape</th>
                        <th className="text-right py-1.5 px-2">Gs ∫</th>
                        <th className="text-right py-1.5 px-2">Dist.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selected.laps.map((l) => {
                        const isBest = l.lap_number === selected.fastest_lap_number;
                        return (
                          <tr
                            key={l.lap_number}
                            className={`border-b border-neutral-800/40 ${isBest ? 'bg-yellow-950/20' : ''}`}
                          >
                            <td className="py-1.5 px-2 text-gray-300">
                              Lap {l.lap_number} {isBest && <span className="ml-1">🏆</span>}
                            </td>
                            <td className={`text-right py-1.5 px-2 ${isBest ? 'text-yellow-300 font-semibold' : 'text-white'}`}>
                              {fmtLap(l.lap_time_s)}
                            </td>
                            <td className="text-right py-1.5 px-2 text-gray-300">{l.max_speed_kmh ?? '—'}</td>
                            <td className="text-right py-1.5 px-2 text-gray-300">{l.max_rpm ?? '—'}</td>
                            <td className="text-right py-1.5 px-2 text-gray-300">{l.max_water_temp_c ?? '—'}</td>
                            <td className="text-right py-1.5 px-2 text-gray-300">{l.max_exhaust_temp_c ?? '—'}</td>
                            <td className="text-right py-1.5 px-2 text-gray-300">{l.lat_g_accumulated ?? '—'}</td>
                            <td className="text-right py-1.5 px-2 text-gray-300">
                              {l.distance_m ? (l.distance_m / 1000).toFixed(2) : '—'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Delta time (only when comparing) */}
          {comparison && (
            <Card className="bg-neutral-900 border-neutral-800">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Trophy className="w-4 h-4 text-yellow-400" />
                  Delta time — Lap {lapB} vs Lap {lapA}
                  <span className="text-xs text-gray-500 ml-2">
                    (negativo = Lap {lapB} más rápida; positivo = Lap {lapA} más rápida)
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="h-44">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={deltaChartData}
                      onMouseMove={(e) => {
                        if (e && e.activeLabel != null) setHoverDistance(e.activeLabel);
                      }}
                      onMouseLeave={() => setHoverDistance(null)}
                      onClick={(e) => {
                        if (e && e.activeLabel != null) pinDistance(e.activeLabel);
                      }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#1f1f1f" />
                      <XAxis
                        dataKey="distance"
                        type="number"
                        domain={zoomRange || ['dataMin', 'dataMax']}
                        allowDataOverflow
                        stroke="#666"
                        fontSize={10}
                        tickFormatter={(v) => `${v}m`}
                      />
                      <YAxis stroke="#666" fontSize={10} tickFormatter={(v) => `${v > 0 ? '+' : ''}${v.toFixed(2)}s`} />
                      <Tooltip
                        contentStyle={{ background: '#0a0a0a', border: '1px solid #333', fontSize: 11 }}
                        formatter={(v) => [`${v > 0 ? '+' : ''}${v.toFixed(3)}s`, 'Delta']}
                        labelFormatter={(v) => `Distancia: ${v}m`}
                      />
                      <ReferenceLine y={0} stroke="#666" strokeDasharray="2 2" />
                      <Line type="monotone" dataKey="delta" stroke="#facc15" strokeWidth={2} dot={false} />
                      {hoverDistance != null && (
                        <ReferenceLine x={hoverDistance} stroke="#9ca3af" strokeDasharray="2 2" isFront />
                      )}
                      {pinnedSelection?.distance != null && (
                        <ReferenceLine
                          x={pinnedSelection.distance}
                          stroke="#22c55e"
                          strokeWidth={2}
                          isFront
                          label={{ value: '◆', fill: '#22c55e', position: 'top', fontSize: 14 }}
                        />
                      )}
                      {annotations.map((a) => (
                        <ReferenceLine
                          key={a.id}
                          x={a.distance}
                          stroke={a.color || '#22c55e'}
                          strokeDasharray="3 2"
                          strokeWidth={1.5}
                          isFront
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Multi-channel comparison plots — Race Studio style stack */}
          <Card className="bg-neutral-900 border-neutral-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2 flex-wrap">
                <Gauge className="w-4 h-4 text-blue-400" />
                Canales por distancia
                <span className="text-[11px] text-gray-500 font-normal ml-1">
                  {lapB ? (
                    <>
                      Lap <span style={{ color: LAP_COLOR_A }}>{lapA}</span>
                      {' vs '}
                      Lap <span style={{ color: LAP_COLOR_B }}>{lapB}</span>
                    </>
                  ) : (
                    <>Lap <span style={{ color: LAP_COLOR_A }}>{lapA}</span></>
                  )}
                </span>
                <div className="ml-auto flex items-center gap-1 flex-wrap" data-testid="channels-toggle">
                  {/* Zoom presets */}
                  <div className="flex items-center gap-1 mr-2 border-r border-neutral-700 pr-2" data-testid="zoom-controls">
                    {(() => {
                      const ranges = lapAData?.samples?.distance_m || [];
                      const maxD = ranges.length ? ranges[ranges.length - 1] : 0;
                      const presets = [
                        { label: '1/4', f: () => setZoomRange([0, maxD / 4]) },
                        { label: '1/2', f: () => setZoomRange([0, maxD / 2]) },
                        {
                          label: '±200m',
                          f: () => {
                            if (hoverDistance != null) {
                              setZoomRange([Math.max(0, hoverDistance - 200), Math.min(maxD, hoverDistance + 200)]);
                            } else {
                              toast.info('Pasá el mouse por un gráfico primero para zoomear ahí', { duration: 1500 });
                            }
                          },
                        },
                      ];
                      return (
                        <>
                          {presets.map((p) => (
                            <button
                              key={p.label}
                              type="button"
                              onClick={p.f}
                              data-testid={`zoom-preset-${p.label}`}
                              className="h-7 px-2 text-[11px] rounded border border-neutral-700 text-gray-400 hover:border-blue-500 hover:text-blue-300"
                            >
                              {p.label}
                            </button>
                          ))}
                          {zoomRange && (
                            <button
                              type="button"
                              onClick={() => setZoomRange(null)}
                              data-testid="zoom-reset"
                              className="h-7 px-2 text-[11px] rounded border border-blue-700 bg-blue-950 text-blue-300 hover:bg-blue-900"
                            >
                              Reset
                            </button>
                          )}
                        </>
                      );
                    })()}
                  </div>
                  {/* "+ Anotación" — Iter 4 redesign: now driven by an
                      explicit *pinned* selection, not by the volatile
                      `hoverDistance`. The user clicks on any chart to fix
                      a green cursor; that selection survives mouse-leave
                      and is the source of truth for the annotation
                      position (and seek-time in Replay Studio). ESC
                      clears it. Showing the picked distance + time next
                      to the button makes the action concrete. */}
                  <button
                    type="button"
                    onClick={() => {
                      if (!pinnedSelection) {
                        toast.info('Hacé click en un gráfico para fijar la posición de la anotación', { duration: 2200 });
                        return;
                      }
                      setAnnDistance(pinnedSelection.distance);
                      setShowAnnInput(true);
                    }}
                    data-testid="add-annotation-btn"
                    aria-disabled={!pinnedSelection}
                    title={pinnedSelection
                      ? `Crear anotación en ${pinnedSelection.distance.toFixed(0)} m${pinnedSelection.time_s != null ? ` · ${pinnedSelection.time_s.toFixed(2)}s` : ''}`
                      : 'Tocá un punto en cualquier gráfico para fijar la posición'}
                    className={`h-7 px-2 text-[11px] rounded border mr-1 transition inline-flex items-center gap-1 ${
                      pinnedSelection
                        ? 'border-green-600 text-green-300 bg-green-950/40 hover:bg-green-900/40'
                        : 'border-neutral-700 text-gray-500 opacity-60 cursor-help'
                    }`}
                  >
                    + Anotación
                    {pinnedSelection && (
                      <span
                        className="font-mono text-[10px] text-green-200"
                        data-testid="pinned-selection-readout"
                      >
                        @ {pinnedSelection.distance.toFixed(0)}m
                        {pinnedSelection.time_s != null && (
                          <> · {pinnedSelection.time_s.toFixed(2)}s</>
                        )}
                      </span>
                    )}
                  </button>
                  {pinnedSelection && (
                    <button
                      type="button"
                      onClick={() => setPinnedSelection(null)}
                      data-testid="pinned-selection-clear"
                      title="Limpiar posición fijada (ESC)"
                      className="h-7 px-2 text-[10px] rounded text-gray-400 hover:text-white border border-neutral-700 hover:border-neutral-500 mr-1"
                    >
                      ✕ ESC
                    </button>
                  )}
                  {/* Channel toggles. All known channels are rendered: the
                      ones absent in this lap's samples appear disabled with
                      a tooltip explaining why (missing sensor / old session /
                      etc.) instead of silently hiding. */}
                  {ALL_CHANNELS.map((c) => {
                    const available = Array.isArray(lapAData?.samples?.[c.key]);
                    const active = visibleChannels.includes(c.key);
                    return (
                      <button
                        key={c.key}
                        type="button"
                        disabled={!available}
                        onClick={() => setVisibleChannels((prev) =>
                          prev.includes(c.key)
                            ? prev.filter((k) => k !== c.key)
                            : [...prev, c.key]
                        )}
                        data-testid={`toggle-channel-${c.key}`}
                        title={available
                          ? `${c.label} (${c.unit})`
                          : `${c.label} — NO DISPONIBLE.\n${CHANNEL_UNAVAILABLE_HINT[c.key] || 'No presente en este archivo.'}`}
                        aria-disabled={!available}
                        className={`h-7 px-2 text-[11px] rounded border transition ${
                          !available
                            ? 'border-neutral-800 text-neutral-600 line-through cursor-not-allowed opacity-50'
                            : active
                              ? 'border-transparent text-black font-semibold'
                              : 'border-neutral-700 text-gray-400 hover:border-neutral-500'
                        }`}
                        style={available && active ? { backgroundColor: c.color } : {}}
                      >
                        {c.label}
                      </button>
                    );
                  })}
                </div>
              </CardTitle>
              {showAnnInput && annDistance != null && (
                <div className="mt-2 flex flex-col gap-2" data-testid="annotation-input-row">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[11px] text-green-400 font-mono">@ {annDistance.toFixed(0)} m</span>
                    {/* Icon picker — same catalogue as Replay Studio. Picking
                        an icon also bumps the colour to match its semantic
                        default (the engineer can override it next). */}
                    <div className="flex items-center gap-1" data-testid="annotation-icon-picker">
                      {ANNOTATION_ICONS.map((ic) => (
                        <button
                          key={ic.key}
                          type="button"
                          onClick={() => { setAnnIcon(ic.key); setAnnColor(ic.color); }}
                          title={ic.label}
                          data-testid={`annotation-icon-${ic.key}`}
                          aria-pressed={annIcon === ic.key}
                          className={`w-7 h-7 rounded text-base leading-none transition flex items-center justify-center ${
                            annIcon === ic.key
                              ? 'bg-neutral-700 ring-2 ring-white/60'
                              : 'bg-neutral-900 hover:bg-neutral-800'
                          }`}
                        >
                          <span aria-hidden>{ic.emoji}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {/* Colour override — only 5 high-contrast shortcuts. The
                        ingeniero can also leave the icon's default colour
                        by simply not touching this row. */}
                    <div className="flex items-center gap-1" data-testid="annotation-color-picker">
                      {[
                        { c: '#22c55e', label: 'Verde · Mejora gral.' },
                        { c: '#ef4444', label: 'Rojo · Frenaje' },
                        { c: '#3b82f6', label: 'Azul · Trazada' },
                        { c: '#eab308', label: 'Amarillo · Acelerador' },
                        { c: '#a855f7', label: 'Violeta · Otro' },
                      ].map(({ c, label }) => (
                        <button
                          key={c}
                          type="button"
                          onClick={() => setAnnColor(c)}
                          title={label}
                          data-testid={`annotation-color-${c}`}
                          className="w-5 h-5 rounded-full border-2 transition"
                          style={{
                            background: c,
                            borderColor: annColor === c ? '#ffffff' : 'transparent',
                            boxShadow: annColor === c ? `0 0 0 1px ${c}` : 'none',
                          }}
                        />
                      ))}
                    </div>
                    <input
                      autoFocus
                      type="text"
                      value={annText}
                      onChange={(e) => setAnnText(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') saveAnnotation(); }}
                      placeholder="Ej: frenó tarde, rebote en T7, cambio largo…"
                      maxLength={500}
                      data-testid="annotation-input"
                      className="flex-1 min-w-[200px] h-8 px-2 text-xs bg-neutral-800 border border-neutral-700 rounded text-white"
                    />
                    <Button
                      type="button"
                      size="sm"
                      onClick={saveAnnotation}
                      disabled={!annText.trim()}
                      data-testid="annotation-save-btn"
                      className="h-8 text-white text-xs"
                      style={{ background: annColor }}
                    >
                      Guardar
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => { setShowAnnInput(false); setAnnText(''); setAnnDistance(null); }}
                      className="h-8 text-xs text-gray-400"
                    >
                      Cancelar
                    </Button>
                  </div>
                </div>
              )}
            </CardHeader>
            <CardContent className="pt-0 space-y-3">
              {channelCharts.length === 0 && (
                <div className="text-xs text-gray-500 py-6 text-center">
                  Activá al menos un canal arriba para verlo graficado.
                </div>
              )}
              {channelCharts.map((c) => {
                const live = valueAt(c.rows, hoverDistance);
                return (
                <div key={c.key} data-testid={`channel-chart-${c.key}`}>
                  <div className="text-[11px] text-gray-400 mb-1 flex items-center gap-2 flex-wrap">
                    <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: c.color }} />
                    {c.label} <span className="text-gray-600">({c.unit})</span>
                    {live && (
                      <span className="ml-auto flex items-center gap-3 font-mono" data-testid={`live-${c.key}`}>
                        <span style={{ color: LAP_COLOR_A }}>
                          {typeof live.valA === 'number' ? live.valA.toFixed(c.key === 'rpm' ? 0 : 2) : '—'}
                        </span>
                        {lapB && (
                          <span style={{ color: LAP_COLOR_B }}>
                            {typeof live.valB === 'number' ? live.valB.toFixed(c.key === 'rpm' ? 0 : 2) : '—'}
                          </span>
                        )}
                      </span>
                    )}
                  </div>                  <div className="h-32">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart
                        data={c.rows}
                        onMouseMove={(e) => {
                          if (e && e.activeLabel != null) setHoverDistance(e.activeLabel);
                        }}
                        onMouseLeave={() => setHoverDistance(null)}
                        onClick={(e) => {
                          if (e && e.activeLabel != null) pinDistance(e.activeLabel);
                        }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#1f1f1f" />
                        <XAxis
                          dataKey="distance"
                          type="number"
                          domain={zoomRange || ['dataMin', 'dataMax']}
                          allowDataOverflow
                          stroke="#666"
                          fontSize={10}
                          tickFormatter={(v) => `${v}m`}
                        />
                        <YAxis stroke="#666" fontSize={10} width={42} />
                        <Tooltip
                          contentStyle={{ background: '#0a0a0a', border: '1px solid #333', fontSize: 11 }}
                          formatter={(v, name) => [
                            typeof v === 'number' ? `${v.toFixed(2)} ${c.unit}` : v,
                            name === 'valA' ? `Lap ${lapA}` : `Lap ${lapB}`,
                          ]}
                          labelFormatter={(v) => `${v}m`}
                        />
                        <Line type="monotone" dataKey="valA" stroke={LAP_COLOR_A} strokeWidth={1.7} dot={false} isAnimationActive={false} />
                        {lapB && (
                          <Line
                            type="monotone"
                            dataKey="valB"
                            stroke={LAP_COLOR_B}
                            strokeWidth={1.4}
                            dot={false}
                            isAnimationActive={false}
                          />
                        )}
                        {hoverDistance != null && (
                          <ReferenceLine x={hoverDistance} stroke="#9ca3af" strokeDasharray="2 2" isFront />
                        )}
                        {pinnedSelection?.distance != null && (
                          <ReferenceLine
                            x={pinnedSelection.distance}
                            stroke="#22c55e"
                            strokeWidth={2}
                            isFront
                          />
                        )}
                        {annotations.map((a) => (
                          a.distance != null && Number.isFinite(a.distance) ? (
                            <ReferenceLine
                              key={a.id}
                              x={a.distance}
                              stroke={a.color || iconFor(a).color}
                              strokeDasharray="3 2"
                              strokeWidth={1.5}
                              isFront
                            />
                          ) : null
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                );
              })}
              {/* ── BUG-1/4/5/6/7/10/12 fixes — annotations list ─────────────
                 - No more `a.distance.toFixed()` crash when distance is null.
                 - Inline edit with the same affordance as Replay Studio.
                 - Filter switch "All" vs "Current lap".
                 - Renders the shared icon catalogue so notes look identical
                   in DAQ and Replay.
                 - Empty state when no annotations match the filter, so the
                   section header doesn't vanish from under the user.
                 - Test IDs unified under `ann-daq-*` to keep both UIs
                   addressable from the same e2e suite. */}
              <div className="pt-3 border-t border-neutral-800" data-testid="annotations-panel-daq">
                <div className="flex items-center justify-between mb-2 gap-2">
                  <div className="text-[11px] text-green-400 uppercase font-semibold flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    Anotaciones ({annotations.length})
                  </div>
                  <div className="flex items-center gap-1 text-[10px]">
                    <button
                      type="button"
                      onClick={() => setAnnFilterLap('all')}
                      data-testid="ann-daq-filter-all"
                      className={`px-2 py-0.5 rounded uppercase tracking-wider ${
                        annFilterLap === 'all'
                          ? 'bg-green-700 text-white'
                          : 'bg-neutral-800 text-gray-400 hover:text-white'
                      }`}
                    >
                      Todas
                    </button>
                    <button
                      type="button"
                      onClick={() => setAnnFilterLap('current')}
                      data-testid="ann-daq-filter-current"
                      className={`px-2 py-0.5 rounded uppercase tracking-wider ${
                        annFilterLap === 'current'
                          ? 'bg-green-700 text-white'
                          : 'bg-neutral-800 text-gray-400 hover:text-white'
                      }`}
                      title={`Sólo notas del Lap ${lapA}`}
                    >
                      Lap {lapA}
                    </button>
                  </div>
                </div>
                {(() => {
                  // Filter + null-safe sort. Mirrors the Replay Studio panel.
                  const visible = annotations.filter((a) =>
                    annFilterLap === 'all' || a.lap_number == null || a.lap_number === lapA,
                  );
                  if (visible.length === 0) {
                    return (
                      <div
                        className="text-[11px] text-gray-500 italic px-2 py-3 text-center"
                        data-testid="ann-daq-empty"
                      >
                        {annotations.length === 0
                          ? 'Sin anotaciones todavía. Pasá el mouse por un gráfico y tocá "+ Anotación".'
                          : `Sin anotaciones para el Lap ${lapA}. Cambiá a "Todas" para verlas.`}
                      </div>
                    );
                  }
                  const sorted = [...visible].sort((a, b) => {
                    const da = Number.isFinite(a.distance) ? a.distance : Infinity;
                    const db = Number.isFinite(b.distance) ? b.distance : Infinity;
                    return da - db;
                  });
                  return (
                    <div className="space-y-1.5">
                      {sorted.map((a) => {
                        const meta = iconFor(a);
                        const tint = a.color || meta.color;
                        const isEditing = editingAnnId === a.id;
                        return (
                          <div
                            key={a.id}
                            data-testid={`ann-daq-row-${a.id}`}
                            className="flex items-center gap-2 text-xs bg-neutral-950 border border-neutral-800 rounded px-2 py-1.5 hover:border-green-700"
                          >
                            <button
                              type="button"
                              onClick={() => { if (Number.isFinite(a.distance)) setHoverDistance(a.distance); }}
                              className="flex items-center gap-2 flex-1 min-w-0 text-left cursor-pointer"
                              title={Number.isFinite(a.distance) ? `Resaltar en gráficos (${a.distance.toFixed(0)} m)` : 'Sin distancia asociada'}
                              data-testid={`ann-daq-highlight-${a.id}`}
                            >
                              <span
                                className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                                style={{ background: tint }}
                                data-testid={`ann-daq-color-${a.id}`}
                              />
                              <span className="font-mono text-green-400 w-12 flex-shrink-0 text-right">
                                {Number.isFinite(a.distance) ? `${a.distance.toFixed(0)}m` : '—'}
                              </span>
                              <span className="text-[14px] flex-shrink-0" aria-hidden>
                                {meta.emoji}
                              </span>
                              {isEditing ? (
                                <input
                                  autoFocus
                                  value={editingAnnText}
                                  onChange={(e) => setEditingAnnText(e.target.value)}
                                  onClick={(e) => e.stopPropagation()}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      const trimmed = editingAnnText.trim();
                                      if (trimmed && trimmed !== a.text) {
                                        editAnnotation(a.id, { text: trimmed });
                                      }
                                      setEditingAnnId(null);
                                    } else if (e.key === 'Escape') {
                                      setEditingAnnId(null);
                                    }
                                  }}
                                  onBlur={() => setEditingAnnId(null)}
                                  className="flex-1 bg-neutral-900 border border-green-700 rounded px-1.5 py-0.5 text-white outline-none min-w-0"
                                  data-testid={`ann-daq-edit-input-${a.id}`}
                                />
                              ) : (
                                <span className="flex-1 text-white truncate">{a.text}</span>
                              )}
                            </button>
                            {a.lap_number != null && (
                              <span className="text-[10px] text-gray-500">L{a.lap_number}</span>
                            )}
                            {!isEditing && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditingAnnId(a.id);
                                  setEditingAnnText(a.text);
                                }}
                                className="text-gray-600 hover:text-yellow-400"
                                data-testid={`ann-daq-edit-${a.id}`}
                                title="Editar texto"
                              >
                                ✏️
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); deleteAnnotation(a.id); }}
                              className="text-gray-600 hover:text-red-400"
                              data-testid={`ann-daq-delete-${a.id}`}
                              title="Borrar"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
            </CardContent>
          </Card>

          {/* Circuit map heatmap */}
          {circuitSvg && (() => {
            const mapMeta = ALL_CHANNELS.find((c) => c.key === mapChannel) || ALL_CHANNELS[0];
            const samplesArr = (lapAData?.samples?.[mapChannel] || []);
            const mapValues = samplesArr.length === circuitSvg.lats.length
              ? samplesArr
              : circuitSvg.lats.map(() => 0);
            // Pick a max for normalisation — for symmetric channels (G's) we
            // take |v|. For positive-only channels (speed, rpm, temps) we use
            // the max directly.
            const isSymmetric = mapChannel === 'lat_g' || mapChannel === 'long_g';
            const norm = Math.max(
              ...mapValues.map((v) => Math.abs(v || 0)),
              0.001,
            );
            const colorFor = (v) => {
              const x = isSymmetric ? Math.abs(v || 0) / norm : (v || 0) / norm;
              const c = Math.max(0, Math.min(1, x));
              // Blue → orange → red ramp (same family as the old G heat scale).
              if (c < 0.5) {
                const t = c / 0.5;
                const r = Math.round(60 + t * (255 - 60));
                return `rgb(${r}, ${Math.round(180 - t * 80)}, ${Math.round(250 - t * 180)})`;
              }
              const t = (c - 0.5) / 0.5;
              return `rgb(255, ${Math.round(100 - t * 100)}, ${Math.round(70 - t * 70)})`;
            };
            const peak = Math.max(...mapValues.map((v) => Math.abs(v || 0)));
            return (
            <Card className="bg-neutral-900 border-neutral-800">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2 flex-wrap">
                  <BarChart3 className="w-4 h-4 text-red-500" />
                  {lapB ? (
                    <>
                      Mapa del circuito — comparación
                      <span className="text-xs text-gray-500 ml-1">
                        <span style={{ color: LAP_COLOR_A }}>Lap {lapA}</span>
                        {' vs '}
                        <span style={{ color: LAP_COLOR_B }}>Lap {lapB}</span>
                      </span>
                    </>
                  ) : (
                    <>
                      Mapa del circuito — coloreado por {mapMeta.label.toLowerCase()}
                      <span className="text-xs text-gray-500 ml-1">
                        azul = bajo, rojo = alto ({peak.toFixed(2)} {mapMeta.unit} pico)
                      </span>
                    </>
                  )}
                  <select
                    value={mapChannel}
                    onChange={(e) => setMapChannel(e.target.value)}
                    data-testid="map-channel-select"
                    disabled={!!lapB}
                    className="ml-2 bg-neutral-800 border border-neutral-700 text-white text-[11px] rounded px-2 py-1 h-7 disabled:opacity-50"
                    title={lapB ? 'En modo comparación el mapa muestra trayectorias por vuelta' : 'Elegí qué canal colorea el trazado'}
                  >
                    {ALL_CHANNELS
                      .filter((c) => Array.isArray(lapAData?.samples?.[c.key]))
                      .map((c) => (
                        <option key={c.key} value={c.key}>
                          {c.label}
                        </option>
                      ))}
                  </select>
                  <div className="ml-auto flex items-center gap-1.5">
                    {/* Map zoom controls */}
                    <div className="flex items-center gap-1 border-r border-neutral-700 pr-1.5" data-testid="map-zoom-controls">
                      <button
                        type="button"
                        onClick={() => setMapZoom((z) => Math.max(1, +(z - 0.5).toFixed(1)))}
                        disabled={mapZoom <= 1}
                        data-testid="map-zoom-out"
                        className="h-7 w-7 text-sm rounded border border-neutral-700 text-gray-300 hover:border-blue-500 disabled:opacity-30"
                        title="Alejar"
                      >−</button>
                      <span className="text-[11px] text-gray-400 w-8 text-center font-mono">{mapZoom.toFixed(1)}x</span>
                      <button
                        type="button"
                        onClick={() => setMapZoom((z) => Math.min(8, +(z + 0.5).toFixed(1)))}
                        data-testid="map-zoom-in"
                        className="h-7 w-7 text-sm rounded border border-neutral-700 text-gray-300 hover:border-blue-500"
                        title="Acercar"
                      >+</button>
                      {mapZoom !== 1 && (
                        <button
                          type="button"
                          onClick={() => setMapZoom(1)}
                          data-testid="map-zoom-reset"
                          className="h-7 px-1.5 text-[10px] rounded border border-blue-700 bg-blue-950 text-blue-300 hover:bg-blue-900"
                        >Reset</button>
                      )}
                    </div>
                    {(selected.format === 'vbo' || selected.format === 'csv') && (
                      pickingStart ? (
                        <>
                          <div className="flex items-center gap-1 text-[10px] text-orange-300">
                            <span>Radio:</span>
                            <input
                              type="range"
                              min={5} max={50} step={1}
                              value={recutRadius}
                              onChange={(e) => setRecutRadius(parseInt(e.target.value, 10))}
                              data-testid="recut-radius-slider"
                              className="w-24"
                            />
                            <span className="w-8 text-orange-200 font-mono">{recutRadius}m</span>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => { setPickingStart(false); setPendingStart(null); }}
                            data-testid="cancel-pick-start-line"
                            className="h-7 text-[11px] border-red-700 text-red-400 hover:bg-red-950"
                          >
                            Cancelar
                          </Button>
                        </>
                      ) : (
                        <>
                          {selected.manual_start_line && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                // Pre-load existing line so the user can adjust radius or move it.
                                setPickingStart(true);
                                const [x, y] = circuitSvg.project(selected.manual_start_line.lat, selected.manual_start_line.lon);
                                setPendingStart({ lat: selected.manual_start_line.lat, lon: selected.manual_start_line.lon, x, y });
                                setRecutRadius(selected.manual_start_line.radius_m || 15);
                              }}
                              data-testid="adjust-start-line-btn"
                              className="h-7 text-[11px] border-green-700 text-green-400 hover:bg-green-950"
                              title="Ajustar la línea existente (mover el punto o cambiar el radio)"
                            >
                              <MapPin className="w-3 h-3 mr-1" />
                              Ajustar línea
                            </Button>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => { setPickingStart(true); setPendingStart(null); }}
                            data-testid="pick-start-line-btn"
                            className="h-7 text-[11px] border-orange-700 text-orange-400 hover:bg-orange-950"
                            title={selected.manual_start_line ? 'Definir otra línea desde cero' : 'Marcá la línea de largada en el mapa para re-cortar las vueltas'}
                          >
                            <MapPin className="w-3 h-3 mr-1" />
                            {selected.manual_start_line ? 'Re-definir' : 'Definir línea de largada'}
                          </Button>
                        </>
                      )
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowSatellite((v) => !v)}
                      data-testid="toggle-satellite-btn"
                      className={`h-7 text-[11px] ${showSatellite ? 'border-emerald-600 text-emerald-300 bg-emerald-950' : 'border-neutral-700 text-gray-400 hover:bg-neutral-900'}`}
                      title="Mostrar/ocultar imagen satelital (Esri)"
                    >
                      {showSatellite ? 'Vector' : 'Satélite'}
                    </Button>
                  </div>
                </CardTitle>
                {pickingStart && (
                  <div className="text-[11px] text-orange-300 mt-1" data-testid="pick-start-hint">
                    Hacé click sobre el trazado donde está la línea de largada/llegada.
                  </div>
                )}
                {selected.manual_start_line && !pickingStart && (
                  <div className="text-[11px] text-gray-500 mt-1">
                    Línea de largada manual activa (radio {selected.manual_start_line.radius_m} m).
                  </div>
                )}
              </CardHeader>
              <CardContent className="pt-0">
                {(() => {
                  // Compute the SVG viewBox, optionally zoomed in around either
                  // the cursor or the centre of the trajectory.
                  const fullW = circuitSvg.W;
                  const fullH = circuitSvg.H;
                  let vbX = 0, vbY = 0, vbW = fullW, vbH = fullH;
                  if (mapZoom > 1) {
                    vbW = fullW / mapZoom;
                    vbH = fullH / mapZoom;
                    // Centre on the cursor if there is one, otherwise on the
                    // overall trajectory centroid.
                    let cx = fullW / 2, cy = fullH / 2;
                    if (cursorOnMap && cursorOnMap.lat != null) {
                      [cx, cy] = circuitSvg.project(cursorOnMap.lat, cursorOnMap.lon);
                    }
                    vbX = Math.max(0, Math.min(fullW - vbW, cx - vbW / 2));
                    vbY = Math.max(0, Math.min(fullH - vbH, cy - vbH / 2));
                  }
                  // Indices for each annotation's nearest sample on lap A
                  const annDots = [];
                  if (lapAData?.samples?.distance_m && lapAData.samples.lat) {
                    for (const a of annotations) {
                      const arr = lapAData.samples.distance_m;
                      let bestI = 0, bestD = Infinity;
                      for (let i = 0; i < arr.length; i++) {
                        const d = Math.abs(arr[i] - a.distance);
                        if (d < bestD) { bestD = d; bestI = i; }
                        else if (arr[i] > a.distance + 200) break;
                      }
                      const lat = lapAData.samples.lat[bestI];
                      const lon = lapAData.samples.lon[bestI];
                      if (lat != null && lon != null) {
                        const [px, py] = circuitSvg.project(lat, lon);
                        annDots.push({ a, x: px, y: py });
                      }
                    }
                  }
                  return (
                <svg
                  viewBox={`${vbX} ${vbY} ${vbW} ${vbH}`}
                  className={`w-full h-auto bg-neutral-950 rounded ${pickingStart ? 'cursor-crosshair' : ''}`}
                  style={{ maxHeight: 500 }}
                  data-testid="circuit-map"
                  onClick={(e) => {
                    if (!pickingStart) return;
                    const rect = e.currentTarget.getBoundingClientRect();
                    const xPx = e.clientX - rect.left;
                    const yPx = e.clientY - rect.top;
                    // viewBox → pixel scale (respect current zoom window)
                    const vx = vbX + (xPx / rect.width) * vbW;
                    const vy = vbY + (yPx / rect.height) * vbH;
                    // Find nearest sample in viewBox space
                    let bestI = 0, bestD = Infinity;
                    for (let i = 0; i < circuitSvg.lats.length; i++) {
                      const [px, py] = circuitSvg.project(circuitSvg.lats[i], circuitSvg.lons[i]);
                      const d = (px - vx) * (px - vx) + (py - vy) * (py - vy);
                      if (d < bestD) { bestD = d; bestI = i; }
                    }
                    const [px, py] = circuitSvg.project(circuitSvg.lats[bestI], circuitSvg.lons[bestI]);
                    setPendingStart({
                      lat: circuitSvg.lats[bestI],
                      lon: circuitSvg.lons[bestI],
                      x: px,
                      y: py,
                    });
                  }}
                >
                  {/* Satellite layer — rendered first so the trajectory sits on top */}
                  {showSatellite && circuitSvg.geoBBox && (
                    <SatelliteTiles
                      bbox={circuitSvg.geoBBox}
                      project={circuitSvg.projectGeo}
                      opacity={0.95}
                    />
                  )}
                  {circuitSvg.lats.map((lat, i) => {
                    if (i === 0) return null;
                    const [x1, y1] = circuitSvg.project(circuitSvg.lats[i - 1], circuitSvg.lons[i - 1]);
                    const [x2, y2] = circuitSvg.project(lat, circuitSvg.lons[i]);
                    // When comparing two laps we draw lap A as a flat magenta
                    // trace (overlay with lap B below); when only A is shown
                    // we keep the colour-mapped heatmap so the user can see
                    // the channel coloured along the trajectory.
                    const strokeColor = lapB ? LAP_COLOR_A : colorFor(mapValues[i]);
                    return (
                      <line
                        key={i}
                        x1={x1} y1={y1} x2={x2} y2={y2}
                        stroke={strokeColor}
                        strokeWidth={lapB ? 2.5 : 4}
                        strokeLinecap="round"
                      />
                    );
                  })}
                  {/* Lap B trajectory overlay (Race-Studio style colour-per-lap) */}
                  {lapB && lapBData?.samples?.lat && (
                    <g data-testid="circuit-map-lap-b">
                      {lapBData.samples.lat.map((lat, i) => {
                        if (i === 0) return null;
                        const [x1, y1] = circuitSvg.project(lapBData.samples.lat[i - 1], lapBData.samples.lon[i - 1]);
                        const [x2, y2] = circuitSvg.project(lat, lapBData.samples.lon[i]);
                        return (
                          <line
                            key={`b-${i}`}
                            x1={x1} y1={y1} x2={x2} y2={y2}
                            stroke={LAP_COLOR_B}
                            strokeWidth={1.7}
                            strokeOpacity={0.85}
                            strokeLinecap="round"
                          />
                        );
                      })}
                    </g>
                  )}
                  {/* Synchronised cursor dot (Race-Studio style) */}
                  {cursorOnMap && cursorOnMap.lat != null && (() => {
                    const [cx, cy] = circuitSvg.project(cursorOnMap.lat, cursorOnMap.lon);
                    return (
                      <g data-testid="circuit-cursor-dot">
                        <circle cx={cx} cy={cy} r={8} fill="none" stroke="#ffffff" strokeWidth={2} opacity={0.85} />
                        <circle cx={cx} cy={cy} r={3} fill="#facc15" />
                      </g>
                    );
                  })()}
                  {/* Permanent marker for the manual start line (if set) */}
                  {selected.manual_start_line && (() => {
                    const [mx, my] = circuitSvg.project(selected.manual_start_line.lat, selected.manual_start_line.lon);
                    const rPx = (selected.manual_start_line.radius_m || 15) * (circuitSvg.pxPerMeter || 0.05);
                    return (
                      <g data-testid="manual-start-marker">
                        <circle cx={mx} cy={my} r={rPx} fill="rgba(34,197,94,0.12)" stroke="#22c55e" strokeWidth={1} strokeDasharray="3 3" />
                        <circle cx={mx} cy={my} r={9} fill="none" stroke="#22c55e" strokeWidth={2} />
                        <circle cx={mx} cy={my} r={3} fill="#22c55e" />
                      </g>
                    );
                  })()}
                  {/* Pending marker while the user is confirming a new line */}
                  {pendingStart && (() => {
                    const rPx = recutRadius * (circuitSvg.pxPerMeter || 0.05);
                    return (
                      <g data-testid="pending-start-marker">
                        <circle cx={pendingStart.x} cy={pendingStart.y} r={rPx} fill="rgba(251,146,60,0.18)" stroke="#fb923c" strokeWidth={1.5} strokeDasharray="3 3" />
                        <circle cx={pendingStart.x} cy={pendingStart.y} r={11} fill="none" stroke="#fb923c" strokeWidth={3} />
                        <circle cx={pendingStart.x} cy={pendingStart.y} r={4} fill="#fb923c" />
                      </g>
                    );
                  })()}
                  {/* Coaching annotations pinned on the trajectory */}
                  {annDots.map(({ a, x, y }) => (
                    <g key={a.id} data-testid={`annotation-pin-${a.id}`}>
                      <circle cx={x} cy={y} r={7} fill={a.color || '#22c55e'} stroke="#0a0a0a" strokeWidth={1.5} />
                      <title>{`${a.distance.toFixed(0)}m — ${a.text}`}</title>
                    </g>
                  ))}
                </svg>
                  );
                })()}
                {pendingStart && (
                  <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="pending-start-confirm">
                    <span className="text-xs text-orange-300">
                      Marcaste el punto. ¿Re-cortar la sesión con esta línea?
                    </span>
                    <Button
                      onClick={recutWithStart}
                      disabled={recutting}
                      size="sm"
                      className="h-8 bg-orange-600 hover:bg-orange-700 text-white text-xs"
                      data-testid="confirm-recut-btn"
                    >
                      {recutting ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <MapPin className="w-3 h-3 mr-1" />}
                      Re-cortar vueltas
                    </Button>
                    <Button
                      onClick={() => setPendingStart(null)}
                      size="sm"
                      variant="ghost"
                      className="h-8 text-xs text-gray-400"
                    >
                      Borrar punto
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
            );
          })()}
        </main>
      </div>
    );
  }

  // ====================== SESSION LIST + UPLOAD =============================
  return (
    <div className="min-h-screen bg-black text-white">
      <header className="border-b border-neutral-800 bg-neutral-900/60 sticky top-0 z-10 backdrop-blur">
        <div className="container mx-auto px-3 md:px-6 py-3 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/')} data-testid="telemetry-back-dash" className="text-gray-400">
            <ArrowLeft className="w-4 h-4 mr-1" />
            Inicio
          </Button>
          <h1 className="text-lg font-bold text-white flex items-center gap-2">
            <Activity className="w-4 h-4 text-blue-500" />
            Telemetría
          </h1>
        </div>
      </header>

      <main className="container mx-auto px-3 md:px-6 py-6 space-y-6">
        {/* Upload card */}
        <Card className="bg-neutral-900 border-neutral-800">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Upload className="w-4 h-4 text-blue-400" />
              Subir archivo de adquisición de datos
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs text-gray-400">Nombre (opcional)</Label>
                <Input
                  value={meta.name}
                  onChange={(e) => setMeta({ ...meta, name: e.target.value })}
                  placeholder="LR - Serie Concordia"
                  data-testid="telemetry-name"
                  className="bg-neutral-800 border-neutral-700 text-white h-9"
                />
              </div>
              <div>
                <Label className="text-xs text-gray-400">Piloto</Label>
                <Input
                  value={meta.pilot}
                  onChange={(e) => setMeta({ ...meta, pilot: e.target.value })}
                  placeholder="Lucio Rodriguez"
                  data-testid="telemetry-pilot"
                  className="bg-neutral-800 border-neutral-700 text-white h-9"
                />
              </div>
              <div>
                <Label className="text-xs text-gray-400">Categoría</Label>
                <select
                  value={meta.category}
                  onChange={(e) => setMeta({ ...meta, category: e.target.value })}
                  data-testid="telemetry-category"
                  className="w-full bg-neutral-800 border border-neutral-700 text-white rounded-md px-3 py-1.5 text-sm h-9"
                >
                  <option value="turismo_pista">Turismo Pista</option>
                  <option value="carshow_santafesino">CarShow Santafesino</option>
                  <option value="turismo_nacional">Turismo Nacional</option>
                  <option value="simracing">Simracing</option>
                  <option value="">Otra</option>
                </select>
              </div>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".vbo,.csv,.txt"
              onChange={onFilePicked}
              disabled={uploading}
              data-testid="telemetry-file-input"
              className="block w-full text-sm text-gray-300 file:mr-3 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-600 file:text-white hover:file:bg-blue-700 file:cursor-pointer"
            />
            <div className="text-[11px] text-gray-500 flex items-start gap-1.5">
              <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              <span>
                Acepta archivos <strong>.vbo</strong> (Racelogic VBOX), <strong>.csv</strong> (AIM RaceStudio export) o <strong>.txt</strong> (genérico).
                Para archivos <strong>.xrk</strong> de AIM exportá primero a CSV desde RaceStudio Analysis.
              </span>
            </div>
            {uploading && (
              <div className="flex items-center gap-2 text-blue-400 text-sm">
                <Loader2 className="w-4 h-4 animate-spin" /> Procesando...
              </div>
            )}
          </CardContent>
        </Card>

        {/* Session list */}
        <Card className="bg-neutral-900 border-neutral-800">
          <CardHeader>
            <CardTitle className="text-base">Sesiones cargadas</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center gap-2 text-gray-500 py-6 justify-center">
                <Loader2 className="w-4 h-4 animate-spin" /> Cargando...
              </div>
            ) : sessions.length === 0 ? (
              <div className="text-center text-gray-500 py-10 text-sm">
                Subí tu primer archivo .vbo o .csv para empezar a analizar.
              </div>
            ) : (
              <div className="space-y-2">
                {sessions.map((s) => (
                  <button
                    key={s.session_id}
                    onClick={() => openSession(s.session_id)}
                    data-testid={`telemetry-session-${s.session_id}`}
                    className="w-full text-left bg-neutral-800/40 hover:bg-neutral-800 border border-neutral-700 rounded-md p-3 transition flex items-center gap-3"
                  >
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${s.format === 'vbo' ? 'bg-green-700/20 border border-green-700/40' : 'bg-blue-700/20 border border-blue-700/40'}`}>
                      <Activity className={`w-4 h-4 ${s.format === 'vbo' ? 'text-green-400' : 'text-blue-400'}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-white text-sm truncate flex items-center gap-2">
                        {s.name}
                        <Badge className="bg-neutral-700 text-white text-[9px] h-4 px-1.5">{s.format?.toUpperCase()}</Badge>
                        {s.category && (
                          <Badge variant="outline" className="text-[9px] h-4 px-1.5 border-neutral-600 text-gray-300">
                            {CATEGORY_LABELS[s.category] || s.category}
                          </Badge>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 flex items-center gap-3 flex-wrap">
                        {s.pilot && <span className="flex items-center gap-1"><User className="w-3 h-3" />{s.pilot}</span>}
                        {s.circuit && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{s.circuit}</span>}
                        <span>· {s.lap_count} vueltas</span>
                        <span className="text-yellow-400">· best {fmtLap(s.fastest_lap_s)}</span>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={(e) => { e.stopPropagation(); navigate(`/replay/session/${s.session_id}`); }}
                      data-testid={`replay-telemetry-${s.session_id}`}
                      title="Replay Studio (video onboard sincronizado)"
                      className="h-8 w-8 text-gray-500 hover:text-red-400"
                    >
                      <Video className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={(e) => deleteSession(s.session_id, e)}
                      data-testid={`delete-telemetry-${s.session_id}`}
                      className="h-8 w-8 text-gray-600 hover:text-red-500"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
